/**
 * Tile 1: Apple Pay, or Google Pay when Apple Pay is unavailable. Tile 2: PayPal.
 * Each candidate's `skin` is the artwork that gets shown - the tile always draws
 * whichever wallet it actually bound to, never a fixed mark.
 */
const TILES = [
  {
    slot: 'wallet-1',
    candidates: [
      { skin: 'apple-pay', selectors: ['shopify-apple-pay-button', 'apple-pay-button'] },
      { skin: 'google-pay', selectors: ['shopify-google-pay-button'] },
    ],
  },
  {
    slot: 'wallet-2',
    candidates: [{ skin: 'paypal', selectors: ['shopify-paypal-button'] }],
  },
];

/** Any wallet Shopify may render into the row. */
const ANY_WALLET =
  'shopify-apple-pay-button, apple-pay-button, shopify-google-pay-button, shopify-paypal-button, shop-pay-wallet-button';

/** How long to wait for Shopify's wallet row before giving up on the skins. */
const WALLET_TIMEOUT_MS = 4000;

/** @type {WeakMap<HTMLElement, ResizeObserver>} */
const resizeObservers = new WeakMap();

/**
 * @param {HTMLElement} native
 * @param {string[]} selectors
 * @returns {HTMLElement | null}
 */
function firstWallet(native, selectors) {
  for (const selector of selectors) {
    const wallet = native.querySelector(selector);
    if (wallet instanceof HTMLElement) return wallet;
  }
  return null;
}

/**
 * Finds the first candidate whose selectors match an actual wallet in the native
 * row, so the tile can draw that candidate's artwork instead of a fixed skin.
 * @param {HTMLElement} native
 * @param {{ skin: string, selectors: string[] }[]} candidates
 * @returns {{ wallet: HTMLElement, skin: string } | null}
 */
function firstBoundWallet(native, candidates) {
  for (const { skin, selectors } of candidates) {
    const wallet = firstWallet(native, selectors);
    if (wallet) return { wallet, skin };
  }
  return null;
}

/**
 * PayPal/Google Pay ignore width:100% from their host. Stretch the hit area onto the tile.
 * @param {HTMLElement} wallet
 * @param {HTMLElement} tile
 */
function fitWalletToTile(wallet, tile) {
  const current = wallet.getBoundingClientRect();
  const target = tile.getBoundingClientRect();
  if (!target.width || !target.height) return;

  /*
    Resetting the transform below resizes the wallet, which wakes the
    ResizeObserver that called us. Stop when it already covers the tile.
  */
  const aligned =
    Math.abs(current.left - target.left) < 0.5 &&
    Math.abs(current.top - target.top) < 0.5 &&
    Math.abs(current.width - target.width) < 0.5 &&
    Math.abs(current.height - target.height) < 0.5;
  if (aligned) return;

  wallet.style.setProperty('transform', 'none', 'important');

  const from = wallet.getBoundingClientRect();
  const to = tile.getBoundingClientRect();
  if (!from.width || !from.height || !to.width || !to.height) return;

  const transform = `translate(${to.left - from.left}px, ${to.top - from.top}px) scale(${to.width / from.width}, ${
    to.height / from.height
  })`;

  wallet.style.setProperty('transform-origin', 'top left', 'important');
  wallet.style.setProperty('transform', transform, 'important');
}

/**
 * Writes only when the value differs: this runs on every page mutation, and a no-op write still dirties layout.
 * @param {HTMLElement} el
 * @param {string} property
 * @param {string} value
 * @param {string} [priority]
 */
function setStyle(el, property, value, priority = '') {
  if (el.style.getPropertyValue(property) === value && el.style.getPropertyPriority(property) === priority) return;
  el.style.setProperty(property, value, priority);
}

/**
 * @param {HTMLElement} el
 * @param {string} property
 */
function clearStyle(el, property) {
  if (el.style.getPropertyValue(property)) el.style.removeProperty(property);
}

/**
 * @param {HTMLElement} root
 */
function layoutWallets(root) {
  const native = root.querySelector('.cart-express-pay__native');
  const visual = root.querySelector('.cart-express-pay__visual');
  if (!(native instanceof HTMLElement) || !(visual instanceof HTMLElement)) return;

  /*
    Shopify injects the wallet row after the drawer paints. Tiles start hidden
    (see the CSS/markup in cart-summary.liquid) precisely so there is nothing to
    blink here - skip the no-op run while the row is still empty, and let the
    settle timeout below force a final pass if no wallet ever shows up.
  */
  if (!native.querySelector(ANY_WALLET) && root.dataset.walletsSettled !== 'true') return;

  let column = 0;
  /** @type {[HTMLElement, HTMLElement][]} */
  const placed = [];
  /** @type {Set<HTMLElement>} */
  const used = new Set();

  for (const { slot, candidates } of TILES) {
    const tile = visual.querySelector(`[data-cart-wallet-slot="${slot}"]`);
    if (!(tile instanceof HTMLElement)) continue;

    const bound = firstBoundWallet(native, candidates);
    if (!bound) {
      if ('cartWalletSkin' in tile.dataset) delete tile.dataset.cartWalletSkin;
      setStyle(tile, 'display', 'none', 'important');
      continue;
    }

    const { wallet, skin } = bound;
    column += 1;
    used.add(wallet);
    if (tile.dataset.cartWalletSkin !== skin) tile.dataset.cartWalletSkin = skin;
    clearStyle(tile, 'display');
    setStyle(tile, 'grid-column', String(column));
    clearStyle(wallet, 'display');
    setStyle(wallet, 'grid-column', String(column), 'important');
    setStyle(wallet, 'grid-row', '1', 'important');
    placed.push([wallet, tile]);
  }

  native.querySelectorAll(ANY_WALLET).forEach((el) => {
    if (!(el instanceof HTMLElement) || used.has(el)) return;
    setStyle(el, 'display', 'none', 'important');
  });

  setStyle(root, '--wallet-tiles', String(Math.max(column, 1)));

  requestAnimationFrame(() => placed.forEach(([wallet, tile]) => fitWalletToTile(wallet, tile)));

  return placed;
}

/**
 * @param {HTMLElement} root
 */
function bindExpressPay(root) {
  if (root.dataset.expressPayBound !== 'true') {
    root.dataset.expressPayBound = 'true';

    const native = root.querySelector('.cart-express-pay__native');
    if (native) {
      new MutationObserver(() => bindExpressPay(root)).observe(native, { childList: true, subtree: true });
    }

    resizeObservers.set(root, new ResizeObserver(() => layoutWallets(root)));

    setTimeout(() => {
      root.dataset.walletsSettled = 'true';
      layoutWallets(root);
    }, WALLET_TIMEOUT_MS);
  }

  const placed = layoutWallets(root) || [];
  const resized = resizeObservers.get(root);
  if (!resized) return;

  resized.observe(root);
  placed.forEach(([wallet, tile]) => {
    resized.observe(wallet);
    resized.observe(tile);
  });
}

function syncAll() {
  document.querySelectorAll('.cart-drawer .cart-express-pay').forEach((root) => {
    if (root instanceof HTMLElement) bindExpressPay(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', syncAll);
} else {
  syncAll();
}

let queued = false;
new MutationObserver(() => {
  if (queued) return;
  queued = true;
  requestAnimationFrame(() => {
    queued = false;
    syncAll();
  });
}).observe(document.body, { childList: true, subtree: true });

document.addEventListener('dialog:open', syncAll, true);
document.addEventListener('cart:update', () => setTimeout(syncAll, 200));
